import {
  TechnicalReportDeviceInterface,
  TechnicalReportImageInterface,
  TechnicalReportInterface,
  WorkOrderDeviceJoinInterface
} from "../../common/interfaces";
import { downloadPdfFrom, previewPdfFrom } from "../../services/pdf.service";
import api from "../../services/api";

export async function createOrGetDraft(
  workOrderId: string | number,
  payload: Partial<TechnicalReportInterface> = {}
): Promise<TechnicalReportInterface> {
  const { data } = await api.post(
    `/work-order/${workOrderId}/technical-report`,
    payload
  );
  return data;
}

export async function updateReport(
  workOrderId: string | number,
  payload: Partial<TechnicalReportInterface>
): Promise<TechnicalReportInterface> {
  const { data } = await api.patch(
    `/work-order/${workOrderId}/technical-report`,
    payload
  );
  return data;
}

export async function completeReport(
  workOrderId: string | number,
  payload: {
    check_out_at?: string;
    services_summary?: string;
    technical_conclusion?: string;
  }
): Promise<TechnicalReportInterface> {
  const { data } = await api.post(
    `/work-order/${workOrderId}/technical-report/complete`,
    payload
  );
  return data;
}

export async function reopenReport(
  reportId: number
): Promise<TechnicalReportInterface> {
  const { data } = await api.post(`/technical-reports/${reportId}/reopen`);
  return data;
}

export async function addDeviceToReport(
  reportId: number,
  workOrderDeviceId: number
): Promise<TechnicalReportDeviceInterface> {
  const { data } = await api.post(
    `/technical-reports/${reportId}/devices/${workOrderDeviceId}`
  );
  return data;
}

export async function listReportDevices(
  reportId: number
): Promise<TechnicalReportDeviceInterface[]> {
  const { data } = await api.get(`/technical-reports/${reportId}/devices`);
  return data;
}

export async function updateReportDevice(
  reportId: number,
  workOrderDeviceId: number,
  technicalData: Record<string, any>
): Promise<TechnicalReportDeviceInterface> {
  const { data } = await api.patch(
    `/technical-reports/${reportId}/devices/${workOrderDeviceId}`,
    { technical_data: technicalData }
  );
  return data;
}

export async function removeDeviceFromReport(
  reportId: number,
  workOrderDeviceId: number
): Promise<void> {
  await api.delete(`/technical-reports/${reportId}/devices/${workOrderDeviceId}`);
}

export async function listImages(
  reportId: number,
  technicalReportDeviceId?: number
): Promise<TechnicalReportImageInterface[]> {
  const { data } = await api.get(`/technical-reports/${reportId}/images`, {
    params: technicalReportDeviceId
      ? { technicalReportDeviceId }
      : undefined
  });
  return data;
}

export async function uploadImages(
  reportId: number,
  technicalReportDeviceId: number,
  files: File[],
  category: string,
  captions?: string[]
): Promise<TechnicalReportImageInterface[]> {
  const formData = new FormData();
  files.forEach((file) => formData.append("images", file));
  formData.append("technical_report_device_id", String(technicalReportDeviceId));
  formData.append("category", category);
  if (captions?.length) formData.append("captions", JSON.stringify(captions));
  const { data } = await api.post(
    `/technical-reports/${reportId}/images`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
}

export async function updateImage(
  reportId: number,
  imageId: number,
  payload: { category?: string; caption?: string }
): Promise<TechnicalReportImageInterface> {
  const { data } = await api.patch(
    `/technical-reports/${reportId}/images/${imageId}`,
    payload
  );
  return data;
}

export async function reorderImages(
  reportId: number,
  items: { technical_report_image_id: number; sort_order: number }[]
): Promise<TechnicalReportImageInterface[]> {
  const { data } = await api.patch(
    `/technical-reports/${reportId}/images/reorder`,
    { items }
  );
  return data;
}

export async function deleteImage(
  reportId: number,
  imageId: number
): Promise<void> {
  await api.delete(`/technical-reports/${reportId}/images/${imageId}`);
}

export async function fetchWorkOrderDevices(
  workOrderId: string | number
): Promise<WorkOrderDeviceJoinInterface[]> {
  const { data } = await api.get(`/work-order/${workOrderId}/all-devices`);
  return data;
}

export async function downloadTechnicalReportPdf(
  workOrderId: string | number,
  workOrderDeviceId?: string | number
) {
  await downloadPdfFrom(
    `/pdf/work-order/${workOrderId}/technical-report${
      workOrderDeviceId ? `?workOrderDeviceId=${workOrderDeviceId}` : ""
    }`,
    {
      overrideServerName: true,
      appendTimestamp: true,
      filename: `relatorio-tecnico-${workOrderId}.pdf`
    }
  );
}

export async function previewTechnicalReportPdf(
  workOrderId: string | number,
  workOrderDeviceId?: string | number
) {
  await previewPdfFrom(
    `/pdf/work-order/${workOrderId}/technical-report${
      workOrderDeviceId ? `?workOrderDeviceId=${workOrderDeviceId}` : ""
    }`
  );
}
